import React, { useState, useEffect } from "react";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSButton from "../../system-design/DSButton";
import DSTextField from "../../system-design/DSTextField";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSIconButton from "../../system-design/DSIconButton";
import { ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { download, constructCloudinaryFrameUrl } from "../../../utils/util";

export type ChooseFramesDialogProps = {
  onHide: () => void;
  animationUrl: string;
};

const FORMAT_OPTIONS = [
  { value: "jpg", label: "JPG" },
  { value: "png", label: "PNG" },
  { value: "webp", label: "WebP" },
  { value: "gif", label: "GIF" },
];

const constructFrameUrl = (
  baseUrl: string,
  format: string,
  isAutoFrame: boolean,
  frameIndex: number,
): string => {
  const frameSelector = isAutoFrame ? "so_auto" : `so_${frameIndex}`;
  return constructCloudinaryFrameUrl(baseUrl, format, frameSelector);
};

const ChooseFramesDialog = ({
  onHide,
  animationUrl,
}: ChooseFramesDialogProps) => {
  const theme = useTheme();
  const [format, setFormat] = useState<string>("jpg");
  const [isAutoFrame, setIsAutoFrame] = useState<boolean>(true);
  const [frameIndex, setFrameIndex] = useState<number>(0);
  const [frameUrl, setFrameUrl] = useState<string>("");

  // Update frame URL whenever parameters change
  useEffect(() => {
    const url = constructFrameUrl(
      animationUrl,
      format,
      isAutoFrame,
      frameIndex,
    );
    setFrameUrl(url);
  }, [animationUrl, format, isAutoFrame, frameIndex]);

  const handlePreviousFrame = () => {
    if (frameIndex > 0) {
      setFrameIndex(frameIndex - 1);
    }
  };

  const handleNextFrame = () => {
    setFrameIndex(frameIndex + 1);
  };

  const handleDownload = () => {
    if (frameUrl) {
      const fileName = `frame_${isAutoFrame ? "auto" : frameIndex}.${format}`;
      download(frameUrl, fileName);
    }
  };

  return (
    <DSDialog open onClose={onHide} maxWidth="md" fullWidth>
      <DSDialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Choose Frame
        <DSIconButton onClick={onHide} aria-label="Close">
          <CloseIcon />
        </DSIconButton>
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 2,
          }}
        >
          {/* Frame Preview Section */}
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              position: "relative",
            }}
          >
            <DSIconButton
              onClick={handlePreviousFrame}
              disabled={isAutoFrame || frameIndex === 0}
              sx={{ position: "absolute", left: 0 }}
            >
              <ArrowBackIosIcon />
            </DSIconButton>

            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                flex: 1,
              }}
            >
              {frameUrl ? (
                <>
                  <img
                    src={frameUrl}
                    alt={`Frame ${isAutoFrame ? "auto" : frameIndex}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "400px",
                      objectFit: "contain",
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.white[12]}`,
                    }}
                    onError={() => {
                      console.error("Failed to load frame image");
                    }}
                  />
                  <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
                    {isAutoFrame ? "Auto Frame" : `Frame ${frameIndex}`}
                  </DSTypography>
                </>
              ) : (
                <DSTypography>Loading frame...</DSTypography>
              )}
            </DSBox>

            <DSIconButton
              onClick={handleNextFrame}
              disabled={isAutoFrame}
              sx={{ position: "absolute", right: 0 }}
            >
              <ArrowForwardIosIcon />
            </DSIconButton>
          </DSBox>

          {/* Controls Section */}
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              paddingTop: 2,
            }}
          >
            <DSBox
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Format Selector */}
              <DSTextField
                select
                label="Format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                sx={{ minWidth: 120 }}
                size="small"
              >
                {FORMAT_OPTIONS.map((option) => (
                  <DSMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </DSMenuItem>
                ))}
              </DSTextField>

              {/* Frame Mode Toggle */}
              <ToggleButtonGroup
                value={isAutoFrame ? "auto" : "manual"}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    setIsAutoFrame(newValue === "auto");
                    if (newValue === "auto") {
                      setFrameIndex(0);
                    }
                  }
                }}
                size="small"
              >
                <ToggleButton value="auto">Auto</ToggleButton>
                <ToggleButton value="manual">Manual</ToggleButton>
              </ToggleButtonGroup>

              {/* Download Button */}
              <DSIconButton
                onClick={handleDownload}
                color="primary"
                title="Download frame"
              >
                <DownloadIcon />
              </DSIconButton>
            </DSBox>

            {/* Frame Index Display (only when not auto) */}
            {!isAutoFrame && (
              <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DSTypography variant="body2">
                  Current Frame Index:
                </DSTypography>
                <DSTextField
                  type="number"
                  value={frameIndex}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setFrameIndex(value);
                    }
                  }}
                  inputProps={{ min: 0, step: 1 }}
                  size="small"
                  sx={{ width: 100 }}
                />
              </DSBox>
            )}
          </DSBox>
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSButton variant="text" type="button" onClick={onHide}>
          Close
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default ChooseFramesDialog;
